import { useCallback, useEffect, useRef, useState } from 'react';
import { useLatest } from 'react-use';

// Buffer after the diagram reports ready, so it has painted (e.g. React Flow
// edges) before we apply the slide's scale transform.
const DEFAULT_SCALE_AFTER_READY_MS = 300;
// Safety net: re-enable scaling even if a remeasure isn't followed by a fresh
// ready signal, so the diagram can never get stuck hidden.
const DEFAULT_RESCALE_FALLBACK_MS = 2000;

interface UseReadyToScaleOptions {
  /**
   * Delay between the diagram reporting ready and the scale being applied,
   * giving it a frame or two to paint. Defaults to 300ms.
   */
  scaleAfterReadyMs?: number;
  /**
   * How long after a remeasure to force the scale back on if no fresh ready
   * signal arrives. Defaults to 2000ms. Use null for async diagrams that must
   * finish measuring before an external scale can be applied.
   */
  rescaleFallbackMs?: number | null;
  /**
   * Optional content/data key. When it changes after the initial mount, the
   * hook automatically remeasures (drops the scale and remounts the diagram) —
   * for diagrams whose data arrives as prop changes (e.g. a refetch or
   * regeneration preview) rather than imperative edit callbacks. Omit it (and
   * call `remeasure` yourself) when changes are driven imperatively, as the
   * family tree slide does. Compose multiple inputs into one string when needed.
   */
  remeasureKey?: string;
}

export interface UseReadyToScaleResult {
  /**
   * Whether the scaled container may apply its scale transform yet. For
   * example, pass to `<PresentationSlide.Slide readyToScale={...} />` (or
   * `ScalableSlide`).
   */
  readyToScale: boolean;
  /**
   * Call when the diagram (re)reports ready — on first render-complete and
   * after every re-layout/remount. Wire this to the diagram's "register ready"
   * callback (e.g. React Flow's `useNodesInitialized` surfaced through a
   * provider). Each call re-measures before re-scaling. If the same diagram
   * reports ready again after the scale is applied, the hook remounts it (see
   * {@link remeasure}), because React Flow measured that layout at the scale.
   */
  registerReady: () => void;
  /**
   * Bump this into the diagram's React `key` so {@link remeasure} (and a
   * {@link UseReadyToScaleOptions.remeasureKey} change) can force a remount.
   */
  remountKey: number;
  /**
   * Call after an edit/refetch that may leave the diagram stale. Holds the
   * scale off and remounts the diagram (via {@link remountKey}) so it
   * re-initializes from scratch, measures cleanly while unscaled, and re-reports
   * ready — the only reliable trigger when an in-place data change doesn't move
   * the diagram's own ready signal (e.g. React Flow's `useNodesInitialized`
   * stays true for a node label edit). A remount is also what clears stale
   * React Flow node internals that otherwise misalign edges after scaling.
   */
  remeasure: () => void;
}

/**
 * Centralizes the "measure-before-scale" dance for React Flow diagrams in a
 * container that uses a CSS scale transform, such as the presentation diagram
 * slides (family tree, estate waterfall, asset distribution, ownership, …).
 * Scaling is driven by the diagram's own readiness signal rather than a
 * fixed timer that can race a slow re-layout and scale a half-measured diagram
 * (which misaligns React Flow edges).
 *
 * Usage:
 * - pass `readyToScale` to the slide and `remountKey` into the diagram's key,
 * - call `registerReady` whenever the diagram reports ready,
 * - call `remeasure` after an edit/refetch/remount-worthy change, or pass a
 *   `remeasureKey` so prop-driven slides remeasure automatically.
 */
export function useReadyToScale({
  scaleAfterReadyMs = DEFAULT_SCALE_AFTER_READY_MS,
  rescaleFallbackMs = DEFAULT_RESCALE_FALLBACK_MS,
  remeasureKey,
}: UseReadyToScaleOptions = {}): UseReadyToScaleResult {
  const [readyToScale, setReadyToScale] = useState(false);
  // Bumped each time the diagram reports ready (first load + every re-layout).
  const [readyCount, setReadyCount] = useState(0);
  // Bumped to remount the diagram (spread into its key).
  const [remountKey, setRemountKey] = useState(0);
  // Last `remeasureKey` we reacted to, so we can detect a change during render.
  const [reactedRemeasureKey, setReactedRemeasureKey] = useState(remeasureKey);
  // Holds the single pending scale timer (scale-after-ready or remount
  // fallback). Shared so either effect cancels the other's timer.
  const scaleTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  );

  const clearScaleTimer = useCallback(() => {
    if (scaleTimerRef.current) {
      clearTimeout(scaleTimerRef.current);
      scaleTimerRef.current = undefined;
    }
  }, []);

  const remeasure = useCallback(() => {
    setReadyToScale(false);
    setRemountKey((key) => key + 1);
  }, []);

  const latestReadyToScale = useLatest(readyToScale);
  const latestRemountKey = useLatest(remountKey);
  // The remount key of the diagram that last reported ready. A match with the
  // current key means that the same diagram mount reports ready again.
  const readyRemountKeyRef = useRef<number | null>(null);

  const registerReady = useCallback(() => {
    const isSameMount = readyRemountKeyRef.current === latestRemountKey.current;
    readyRemountKeyRef.current = latestRemountKey.current;

    // The diagram did a new layout after the slide applied its scale. React
    // Flow measures node handles from client rects, which include the slide
    // scale, so the edges do not align with the nodes. Removing the scale does
    // not measure the handles again. Remount the diagram, so that it measures
    // without the scale and then reports ready again.
    if (isSameMount && latestReadyToScale.current) {
      remeasure();
      return;
    }

    setReadyCount((count) => count + 1);
  }, [latestReadyToScale, latestRemountKey, remeasure]);

  // On a content-key change, drop the scale and remount during render (not in a
  // post-paint effect) so both land in the same commit as the change —
  // otherwise the stale scale stays applied to the updating diagram for a frame.
  // No-ops on the initial mount, since the keys start equal.
  if (remeasureKey !== reactedRemeasureKey) {
    setReactedRemeasureKey(remeasureKey);
    setReadyToScale(false);
    setRemountKey((key) => key + 1);
  }

  // Apply the scale once the diagram reports ready and has had a frame to paint.
  useEffect(() => {
    if (readyCount === 0) {
      return;
    }

    clearScaleTimer();

    if (scaleAfterReadyMs === 0) {
      setReadyToScale(true);
      return;
    }

    setReadyToScale(false);
    scaleTimerRef.current = setTimeout(
      () => setReadyToScale(true),
      scaleAfterReadyMs
    );

    return clearScaleTimer;
  }, [readyCount, scaleAfterReadyMs, clearScaleTimer]);

  // Safety net: after a remount, force the scale back on if no fresh ready
  // arrives. Re-running cancels a pending scale-after-ready timer from an
  // earlier `registerReady`, so it can't flip the scale on mid-remount. Skips
  // the initial mount (`remountKey === 0`).
  useEffect(() => {
    if (remountKey === 0) {
      return;
    }

    clearScaleTimer();
    if (rescaleFallbackMs === null) {
      return;
    }

    scaleTimerRef.current = setTimeout(
      () => setReadyToScale(true),
      rescaleFallbackMs
    );

    return clearScaleTimer;
  }, [remountKey, rescaleFallbackMs, clearScaleTimer]);

  return { readyToScale, registerReady, remountKey, remeasure };
}
